import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Database,
  FileJson,
  FolderOpen,
  Image,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Copy,
  Check,
  Layers,
} from 'lucide-react';
import { MONAIDataset, MONAITaskType, MONAIModality, MONAIDatasetFormat } from '@/mock/gpu-instance';

interface MONAIDatasetManagerProps {
  datasets: MONAIDataset[];
  onSelectDataset?: (dataset: MONAIDataset) => void;
  onViewDatasetCard?: (dataset: MONAIDataset) => void;
  selectedDatasetId?: string;
  filterTask?: MONAITaskType;
  filterModality?: MONAIModality;
}

const getFormatIcon = (format: MONAIDatasetFormat) => {
  switch (format) {
    case 'nifti':
      return <FileJson className="h-3 w-3 text-blue-500" />;
    case 'dicom':
      return <FolderOpen className="h-3 w-3 text-purple-500" />;
    case 'png':
      return <Image className="h-3 w-3 text-green-500" />;
    case 'medmnist':
      return <Database className="h-3 w-3 text-orange-500" />;
  }
};

const getFormatBadge = (format: MONAIDatasetFormat) => {
  const colors: Record<MONAIDatasetFormat, string> = {
    nifti: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    dicom: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
    png: 'bg-green-500/10 text-green-600 border-green-500/30',
    medmnist: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
  };
  return (
    <Badge variant="outline" className={colors[format]}>
      {getFormatIcon(format)}
      <span className="ml-1">{format.toUpperCase()}</span>
    </Badge>
  );
};

const getModalityBadge = (modality: MONAIModality) => {
  const colors: Record<MONAIModality, string> = {
    CT: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    MRI: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
    'X-Ray': 'bg-cyan-500/10 text-cyan-600 border-cyan-500/30',
    Ultrasound: 'bg-green-500/10 text-green-600 border-green-500/30',
    Pathology: 'bg-pink-500/10 text-pink-600 border-pink-500/30',
    Fundus: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    Dermoscopy: 'bg-red-500/10 text-red-600 border-red-500/30',
    Endoscopy: 'bg-teal-500/10 text-teal-600 border-teal-500/30',
  };
  return <Badge variant="outline" className={colors[modality]}>{modality}</Badge>;
};

const getTaskBadge = (task: MONAITaskType) => {
  switch (task) {
    case 'segmentation':
      return (
        <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
          <Layers className="h-3 w-3 mr-1" /> Segmentation
        </Badge>
      );
    case 'classification':
      return (
        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">
          Classification
        </Badge>
      );
    case 'detection':
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
          Detection
        </Badge>
      );
    case 'registration':
      return (
        <Badge className="bg-orange-500/10 text-orange-600 border-orange-500/30">
          Registration
        </Badge>
      );
  }
};

const getStatusBadge = (status: MONAIDataset['status']) => {
  switch (status) {
    case 'ready':
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
          <CheckCircle2 className="h-3 w-3 mr-1" /> Ready
        </Badge>
      );
    case 'processing':
      return (
        <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Processing
        </Badge>
      );
    case 'error':
      return (
        <Badge className="bg-red-500/10 text-red-600 border-red-500/30">
          <AlertCircle className="h-3 w-3 mr-1" /> Error
        </Badge>
      );
  }
};

const MONAIDatasetManager = ({
  datasets,
  onSelectDataset,
  onViewDatasetCard,
  selectedDatasetId,
  filterTask: initialFilterTask,
  filterModality: initialFilterModality,
}: MONAIDatasetManagerProps) => {
  const [copiedPath, setCopiedPath] = useState<string | null>(null);
  const [filterTask, setFilterTask] = useState<string>(initialFilterTask || 'all');
  const [filterModality, setFilterModality] = useState<string>(initialFilterModality || 'all');
  const [sortBy, setSortBy] = useState<string>('size');

  const handleCopyPath = async (path: string) => {
    try {
      await navigator.clipboard.writeText(path);
      setCopiedPath(path);
      setTimeout(() => setCopiedPath(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const filteredDatasets = datasets
    .filter((dataset) => {
      if (filterTask !== 'all' && dataset.task !== filterTask) return false;
      if (filterModality !== 'all' && dataset.modality !== filterModality) return false;
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'size':
          return b.trainCount - a.trainCount;
        case 'name':
          return a.displayName.localeCompare(b.displayName);
        default:
          return 0;
      }
    });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4">
        <Select value={filterTask} onValueChange={setFilterTask}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by task" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tasks</SelectItem>
            <SelectItem value="segmentation">Segmentation</SelectItem>
            <SelectItem value="classification">Classification</SelectItem>
            <SelectItem value="detection">Detection</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterModality} onValueChange={setFilterModality}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by modality" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modalities</SelectItem>
            <SelectItem value="CT">CT</SelectItem>
            <SelectItem value="MRI">MRI</SelectItem>
            <SelectItem value="X-Ray">X-Ray</SelectItem>
            <SelectItem value="Ultrasound">Ultrasound</SelectItem>
            <SelectItem value="Pathology">Pathology</SelectItem>
            <SelectItem value="Fundus">Fundus</SelectItem>
            <SelectItem value="Dermoscopy">Dermoscopy</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="size">Largest First</SelectItem>
            <SelectItem value="name">Name (A-Z)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Dataset List */}
      <div className="space-y-3">
        {filteredDatasets.map((dataset) => (
          <Card
            key={dataset.id}
            className={`cursor-pointer transition-all hover:border-primary/50 ${
              selectedDatasetId === dataset.id ? 'border-primary ring-1 ring-primary' : ''
            }`}
            onClick={() => onViewDatasetCard?.(dataset)}
          >
            <CardContent className="pt-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/10 to-cyan-500/10">
                    <Database className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {dataset.displayName}
                      {getStatusBadge(dataset.status)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {dataset.description}
                    </div>
                  </div>
                </div>
                {getFormatBadge(dataset.format)}
              </div>

              {/* Task & Modality */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {getTaskBadge(dataset.task)}
                {getModalityBadge(dataset.modality)}
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground">Train</div>
                  <div className="font-mono font-medium text-sm">
                    {dataset.trainCount.toLocaleString()}
                  </div>
                </div>
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground">Val</div>
                  <div className="font-mono font-medium text-sm">
                    {dataset.valCount.toLocaleString()}
                  </div>
                </div>
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground">Test</div>
                  <div className="font-mono font-medium text-sm">
                    {dataset.testCount?.toLocaleString() || '-'}
                  </div>
                </div>
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground">Classes</div>
                  <div className="font-mono font-medium text-sm">
                    {dataset.classes.length}
                  </div>
                </div>
              </div>

              {/* Classes Preview */}
              <div className="mb-3">
                <div className="text-xs text-muted-foreground mb-1">Classes:</div>
                <div className="flex flex-wrap gap-1">
                  {dataset.classes.slice(0, 5).map((cls) => (
                    <Badge key={cls} variant="secondary" className="text-xs">
                      {cls}
                    </Badge>
                  ))}
                  {dataset.classes.length > 5 && (
                    <Badge variant="outline" className="text-xs">
                      +{dataset.classes.length - 5} more
                    </Badge>
                  )}
                </div>
              </div>

              {/* Path & Size */}
              <div className="flex items-center justify-between bg-muted/50 p-2 rounded">
                <div className="flex items-center gap-2">
                  <code className="text-xs text-muted-foreground truncate max-w-[200px]">
                    {dataset.path}
                  </code>
                  <span className="text-xs text-muted-foreground">|</span>
                  <span className="text-xs font-medium">{dataset.totalSize}</span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyPath(dataset.path);
                  }}
                >
                  {copiedPath === dataset.path ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredDatasets.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="pt-6 text-center">
              <Database className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No datasets match your filters</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MONAIDatasetManager;
